import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useV2List } from "@/hooks/useTransfersV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LocationCombobox, { LocationOption } from "@/components/LocationCombobox";
import { MapPin, Calendar, Users, Search } from "lucide-react";
import { toast } from "sonner";

export default function HeroSearchV2() {
  const navigate = useNavigate();
  const { data: airports = [] } = useV2List("transfer_airports_v2");
  const { data: hotels = [] } = useV2List("transfer_hotels_v2");

  const [tripType, setTripType] = useState<"oneway" | "roundtrip">("oneway");
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [passengers, setPassengers] = useState(2);

  const options: LocationOption[] = useMemo(() => {
    const a = airports.map((x: any) => ({ id: `airport:${x.id}`, name: `${x.name} (${x.code})`, lat: Number(x.latitude), lng: Number(x.longitude), kind: "airport" as const }));
    const h = hotels.map((x: any) => ({ id: `hotel:${x.id}`, name: x.name, lat: Number(x.latitude), lng: Number(x.longitude), kind: "hotel" as const, zone_id: x.zone_id }));
    return [...a, ...h];
  }, [airports, hotels]);


  const handleSearch = () => {
    if (!fromId || !toId) { toast.error("Please select origin and destination"); return; }
    if (fromId === toId) { toast.error("Origin and destination must differ"); return; }
    if (!pickupDate || !pickupTime) { toast.error("Please select pickup date and time"); return; }
    if (tripType === "roundtrip" && (!returnDate || !returnTime)) { toast.error("Please select return date/time"); return; }
    const params = new URLSearchParams({
      fromId, toId, pickupDate, pickupTime, tripType,
      passengers: String(passengers),
    });
    if (tripType === "roundtrip") {
      params.set("returnDate", returnDate);
      params.set("returnTime", returnTime);
    }
    navigate(`/transfers?${params.toString()}`);
  };

  return (
    <div className="bg-black/85 backdrop-blur-xl border border-primary/30 rounded-2xl p-6 shadow-2xl shadow-primary/10">
      {/* Trip type tabs */}
      <div className="flex gap-2 mb-4 p-1 bg-white/5 rounded-lg">
        <button
          onClick={() => setTripType("oneway")}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
            tripType === "oneway" ? "bg-gradient-to-r from-primary to-secondary text-black" : "text-white/70 hover:text-white"
          }`}
        >
          One Way
        </button>
        <button
          onClick={() => setTripType("roundtrip")}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
            tripType === "roundtrip" ? "bg-gradient-to-r from-primary to-secondary text-black" : "text-white/70 hover:text-white"
          }`}
        >
          Round Trip
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs text-white/70 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> From</Label>
          <LocationCombobox
            value={fromId}
            onChange={setFromId}
            options={options}
            exclude={toId}
            placeholder="Pickup location"
            className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
          />
        </div>

        <div>
          <Label className="text-xs text-white/70 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> To</Label>
          <LocationCombobox
            value={toId}
            onChange={setToId}
            options={options}
            exclude={fromId}
            placeholder="Drop-off location"
            className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
          />
        </div>


        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-white/70 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date</Label>
            <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)}
              onClick={(e) => (e.currentTarget as any).showPicker?.()}
              onFocus={(e) => (e.currentTarget as any).showPicker?.()}
              className="dark-picker bg-white/5 border-white/10 text-white h-11 cursor-pointer [color-scheme:dark]" />
          </div>
          <div>
            <Label className="text-xs text-white/70 mb-1">Time</Label>
            <Input type="time" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)}
              onClick={(e) => (e.currentTarget as any).showPicker?.()}
              onFocus={(e) => (e.currentTarget as any).showPicker?.()}
              className="dark-picker bg-white/5 border-white/10 text-white h-11 cursor-pointer [color-scheme:dark]" />
          </div>
        </div>

        {tripType === "roundtrip" && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-white/70 mb-1">Return Date</Label>
              <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                onClick={(e) => (e.currentTarget as any).showPicker?.()}
                onFocus={(e) => (e.currentTarget as any).showPicker?.()}
                className="dark-picker bg-white/5 border-white/10 text-white h-11 cursor-pointer [color-scheme:dark]" />
            </div>
            <div>
              <Label className="text-xs text-white/70 mb-1">Return Time</Label>
              <Input type="time" value={returnTime} onChange={(e) => setReturnTime(e.target.value)}
                onClick={(e) => (e.currentTarget as any).showPicker?.()}
                onFocus={(e) => (e.currentTarget as any).showPicker?.()}
                className="dark-picker bg-white/5 border-white/10 text-white h-11 cursor-pointer [color-scheme:dark]" />
            </div>
          </div>
        )}


        <div>
          <Label className="text-xs text-white/70 mb-1 flex items-center gap-1"><Users className="w-3 h-3" /> Passengers</Label>
          <Input type="number" min={1} max={20} value={passengers}
            onChange={(e) => setPassengers(Math.max(1, Number(e.target.value) || 1))}
            className="bg-white/5 border-white/10 text-white h-11" />
        </div>

        <Button
          onClick={handleSearch}
          className="w-full h-12 bg-gradient-to-r from-primary via-secondary to-primary text-black font-bold hover:opacity-90 shadow-lg shadow-primary/30"
        >
          <Search className="w-4 h-4 mr-2" />
          Search Transfers
        </Button>
      </div>
    </div>
  );
}
